import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils.js';
import { GripVertical, Trash2, Plus, Copy, Check } from 'lucide-react';

const Block = ({ block, onUpdate, onDelete, onAddAfter, onKeyDown, onSlashTrigger, index, totalBlocks }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const autoResize = useCallback(() => {
    const textarea = ref.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    autoResize();
  }, [block.content, block.type, autoResize]);

  useEffect(() => {
    if (block.shouldFocus && ref.current) {
      ref.current.focus();
      onUpdate(block.id, { shouldFocus: false });
    }
  }, [block.shouldFocus]);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    // On mobile screens, keydown events with '/' might not fire properly on virtual keyboards.
    // Intercept character input here instead to trigger slash command menu.
    if (val === '/' && block.content === '') {
      onUpdate(block.id, { content: '' });
      onSlashTrigger?.(block.id);
      return;
    }
    onUpdate(block.id, { content: val });
    autoResize();
  }, [block.id, block.content, onUpdate, onSlashTrigger, autoResize]);

  const handleKeyDown = useCallback((e) => {
    console.log('[Block] KeyDown:', e.key, 'block:', block.id, 'content:', block.content);
    
    if (e.key === 'Enter' && !e.shiftKey && block.type !== 'code') {
      e.preventDefault();
      onAddAfter(block.id, { type: 'text', content: '' });
    } else if (e.key === 'Backspace' && block.content === '' && index > 0) {
      e.preventDefault();
      onDelete(block.id);
    } else if (e.key === 'ArrowUp' && index > 0 && ref.current?.selectionStart === 0) {
      e.preventDefault();
      const prevBlock = document.querySelector(`[data-block-index="${index - 1}"]`);
      if (prevBlock) {
        const textarea = prevBlock.querySelector('textarea');
        if (textarea) textarea.focus();
      }
    } else if (e.key === 'ArrowDown' && index < totalBlocks - 1) {
      const textarea = ref.current;
      if (textarea && textarea.selectionStart === textarea.value.length) {
        e.preventDefault();
        const nextBlock = document.querySelector(`[data-block-index="${index + 1}"]`);
        if (nextBlock) {
          const nextTextarea = nextBlock.querySelector('textarea');
          if (nextTextarea) nextTextarea.focus();
        }
      }
    } else if (e.key === '/' && block.content === '') {
      console.log('[Block] Slash on empty block, preventing default');
      e.preventDefault();
    }
    onKeyDown?.(e, block);
  }, [block, index, totalBlocks, onAddAfter, onDelete, onKeyDown]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setShowActions(true);
    autoResize();
  }, [autoResize]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    setTimeout(() => setShowActions(false), 200);
  }, []);

  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(block.content || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = block.content || '';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [block.content]);

  const renderContent = () => {
    const baseClasses = 'w-full bg-transparent border-none outline-none resize-none text-zinc-100 placeholder:text-zinc-500 overflow-hidden';
    
    switch (block.type) {
      case 'heading1':
        return (
          <textarea
            ref={ref}
            className={cn(baseClasses, 'text-3xl font-bold text-zinc-50 placeholder:text-zinc-700')}
            value={block.content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Heading 1"
            rows={1}
          />
        );
      case 'heading2':
        return (
          <textarea
            ref={ref}
            className={cn(baseClasses, 'text-2xl font-semibold text-zinc-100 placeholder:text-zinc-700')}
            value={block.content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Heading 2"
            rows={1}
          />
        );
      case 'heading3':
        return (
          <textarea
            ref={ref}
            className={cn(baseClasses, 'text-xl font-medium text-zinc-200 placeholder:text-zinc-700')}
            value={block.content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Heading 3"
            rows={1}
          />
        );
      case 'checklist':
        return (
          <div className="flex items-start gap-3 py-1">
            <input
              type="checkbox"
              checked={block.checked || false}
              onChange={(e) => onUpdate(block.id, { checked: e.target.checked })}
              className="mt-1 h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-accent focus:ring-accent cursor-pointer"
            />
            <textarea
              ref={ref}
              className={cn(baseClasses, block.checked ? 'line-through text-zinc-500' : 'text-zinc-200')}
              value={block.content}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder="To-do"
              rows={1}
            />
          </div>
        );
      case 'quote':
        return (
          <div className="border-l-4 border-accent/60 pl-4 py-1 bg-accent/5 rounded-r-lg">
            <textarea
              ref={ref}
              className={cn(baseClasses, 'italic text-zinc-300 placeholder:text-zinc-600')}
              value={block.content}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder="Write a quote..."
              rows={1}
            />
          </div>
        );
      case 'code':
        return (
          <div className="rounded-lg bg-zinc-900 border border-zinc-700/50 overflow-hidden group/code">
            <div className="flex items-center justify-between px-4 py-1.5 bg-zinc-800/50 border-b border-zinc-700/50">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-600"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-600"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-600"></div>
                </div>
                <span className="text-[10px] text-zinc-500 font-mono">code</span>
              </div>
              <button
                onClick={handleCopyCode}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-zinc-400 hover:text-zinc-200 hover:bg-white/10 transition opacity-0 group-hover/code:opacity-100"
                title="Copy code"
              >
                {copied ? (
                  <>
                    <Check size={12} className="text-emerald-400" />
                    <span className="text-emerald-400">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={12} />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <pre className="p-4 font-mono text-sm leading-relaxed">
              <textarea
                ref={ref}
                className="w-full bg-transparent border-none outline-none resize-none text-emerald-400 placeholder:text-zinc-600 font-mono text-sm leading-relaxed"
                value={block.content}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder="// Write your code here..."
                rows={1}
                spellCheck={false}
              />
            </pre>
          </div>
        );
      case 'divider':
        return <hr className="border-border/40 my-4" />;
      case 'page':
        return null;
      default:
        return (
          <textarea
            ref={ref}
            className={cn(baseClasses)}
            value={block.content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Type '/' for commands..."
            rows={1}
          />
        );
    }
  };

  if (block.type === 'divider') {
    return (
      <div
        data-block-index={index}
        className={cn('group relative flex items-center py-1', isFocused && 'bg-accent/5 rounded')}
      >
        {showActions && (
          <div className={cn(
            "absolute -left-8 top-1/2 -translate-y-1/2 transition-opacity",
            isFocused ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}>
            <button
              onClick={() => onAddAfter(block.id, { type: 'text', content: '' })}
              className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-zinc-300"
            >
              <Plus size={14} />
            </button>
          </div>
        )}
        {renderContent()}
      </div>
    );
  }

  return (
    <div
      data-block-index={index}
      className={cn(
        'group relative flex items-start gap-2 py-1 rounded transition',
        isFocused && 'bg-accent/5'
      )}
    >
      <div className={cn(
        "flex items-center gap-1 transition-opacity pt-1",
        isFocused ? "opacity-100" : "opacity-0 group-hover:opacity-100"
      )}>
        <button
          onClick={() => onAddAfter(block.id, { type: 'text', content: '' })}
          className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-zinc-300"
          title="Add block"
        >
          <Plus size={14} />
        </button>
        <button
          onClick={() => onDelete(block.id)}
          className="p-1 rounded hover:bg-rose-500/20 text-zinc-500 hover:text-rose-400"
          title="Delete block"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        {renderContent()}
      </div>
    </div>
  );
};

export default Block;
